'use client'

import { omit } from 'lodash'
import { useState } from 'react'

import {
  REGISTRATION_FORM_STORAGE_KEY,
  BusinessRegistrationRepresentativeDto,
  useFetchBusinessAddresses,
} from '@marketplace-web/business-accounts/business-accounts-data'
import {
  KybFormFields,
  userKybSuccessEvent,
} from '@marketplace-web/pay-identity/pay-identity-kyc-data'

import {
  PaymentIdentityUboModel,
  PaymentsIdentityConfigurationModel,
  PaymentsIdentityFileModel,
  Status,
  submitPaymentsIdentity,
  SubmitPaymentsIdentityArgs,
  transformKybData,
  transformPaymentsIdentityUbo,
} from '@marketplace-web/pay-identity/payments-identity-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { removeLocalStorageItem } from '@marketplace-web/browser/browser-storage-util'
import { navigateToPage } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { AddressModel } from '@marketplace-web/address/address-data'

import { isFieldRequired } from './utils'
import { KybFieldName } from '../../constants'
import { MEMBER_PROFILE_URL } from '../../constants/routes'
import { transformPaymentsIdentityConfiguration } from '../../transformers'

export type KybFormData = {
  firstName: string | null
  lastName: string | null
  birthdate: string | null
  address: AddressModel | null
  isLegalRepresentativeUbo: boolean
  ubos: Array<PaymentIdentityUboModel> | null
  documents: Array<PaymentsIdentityFileModel> | null
  identityDocumentExpiryDate: string | null
}
const getKybData = (kybFormData: KybFormData, requiredFields: Array<string>) => {
  const isUboRequired = isFieldRequired(requiredFields, KybFieldName.Ubos)

  // Expiry date field will not be sent to MangoPay, we save it to our database instead.
  const mangoPayKybData = omit(kybFormData, KybFormFields.IdentityDocumentExpiryDate)

  if (isUboRequired && kybFormData.isLegalRepresentativeUbo) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const firstUbo = kybFormData.ubos?.[0]! || {
      nationality: '',
      birthplaceCountry: '',
      birthplaceCity: '',
    }

    return {
      ...mangoPayKybData,
      ubos: [{ ...firstUbo, firstName: '', lastName: '', birthday: '', address: {} }],
    }
  }

  return mangoPayKybData
}

const useSubmitKybFormRequest = () => {
  const { track } = useTracking()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Array<ErrorItem> | null>([])
  const [showGeneralError, setShowGeneralError] = useState(false)
  const [data, setData] = useState<PaymentsIdentityConfigurationModel | null>(null)
  const { user } = useSession()
  const { businessAddressesInitialResponse, businessAccountId } = useFetchBusinessAddresses()
  const businessAddress = businessAddressesInitialResponse?.addresses.business_address

  const submitKybForm = async ({
    kybFormData,
    requiredFields,
    representativeData,
  }: {
    kybFormData: KybFormData
    requiredFields: Array<string>
    representativeData?: BusinessRegistrationRepresentativeDto
  }) => {
    if (!businessAccountId || !businessAddress || !user?.id) {
      setShowGeneralError(true)

      return
    }

    const kybData = getKybData(kybFormData, requiredFields)

    if (!kybData) {
      setShowGeneralError(true)
      setLoading(false)

      return
    }

    const { isLegalRepresentativeUbo } = kybFormData
    const isUboRequired = isFieldRequired(requiredFields, KybFieldName.Ubos)
    const isNationalityRequired = isFieldRequired(requiredFields, KybFieldName.Nationality)
    let processedKybData: SubmitPaymentsIdentityArgs

    setLoading(true)
    setError(null)

    try {
      if (
        kybData?.ubos &&
        isLegalRepresentativeUbo &&
        isUboRequired &&
        businessAddress &&
        representativeData
      ) {
        processedKybData = {
          ...transformKybData(kybData, representativeData, businessAddress),
          ubos: transformPaymentsIdentityUbo(kybData, representativeData),
        }
      } else {
        if (!representativeData || !businessAddress) {
          setShowGeneralError(true)
          setLoading(false)

          return
        }
        processedKybData = transformKybData(kybData, representativeData, businessAddress)
      }

      if (!isNationalityRequired) {
        const { nationality, ...restData } = processedKybData

        processedKybData = restData
      }

      const response = await submitPaymentsIdentity(processedKybData)

      if ('errors' in response) {
        setError(response.errors)

        if (!response.errors.length) setShowGeneralError(true)
      } else {
        const configuration = transformPaymentsIdentityConfiguration(response)
        setData(configuration)
        removeLocalStorageItem(REGISTRATION_FORM_STORAGE_KEY)
        const kybSuccessEvent = userKybSuccessEvent({
          businessAccountId,
        })
        track(kybSuccessEvent)

        const isKybFormStatusInvalid = [
          Status.Unverified,
          Status.VerificationFailed,
          Status.VerificationBlocked,
        ].includes(response.payments_identity.status)

        if (isKybFormStatusInvalid) {
          setLoading(false)
        } else {
          navigateToPage(MEMBER_PROFILE_URL(user.id))
        }
      }
    } catch (err) {
      setError(err)
      setLoading(false)
    }
  }

  return { loading, error, showGeneralError, data, submitKybForm }
}

export default useSubmitKybFormRequest
